import React, { useMemo } from 'react';
import { toast } from 'react-toastify';

import { Button } from '@/components/ui/button';
import {
  useRefreshSitrepV2,
  useUpdateSitrepSectionV2,
} from 'components/DisasterGPT/hooks/useSitrepsV2';
import { ReportsGeneratingPlaceholder } from 'components/Reports/ReportsGeneratingPlaceholder';
import type { GetSitrepsResponse } from 'components/Reports/reports.types';
import {
  ActionsResults,
  IncidentsResults,
  OrgEventsResults,
  parseEntityListPayload,
  type ActionsPayload,
  type IncidentsPayload,
  type OrgEventsPayload,
} from './EntityListSectionResults';
import { type PersistedAreaScopeEntry } from './reportAreaScope';
import { useAorText } from 'utils/aorTerminology';

export const ORG_EVENTS_IN_AREA_SOURCE = 'Org Events In Area';
export const INCIDENTS_IN_AREA_SOURCE = 'Incidents In Area';
export const ACTIONS_IN_AREA_SOURCE = 'Actions In Area';

type EntityListKind = 'orgEvents' | 'incidents' | 'actions';

type SitrepSectionLike = {
  id: string;
  type?: string;
  data_sources?: Array<PersistedAreaScopeEntry & { source?: string }>;
  automation?: { provider?: string; [key: string]: unknown };
  output_format_structured?: {
    automation?: { provider?: string; [key: string]: unknown };
    [key: string]: unknown;
  };
  section_versions?: Array<{
    data_string?: string | null;
    sitrep_section_text?: string | null;
    status?: string | null;
    timestamp?: string | null;
  }>;
};

const KIND_CONFIG = {
  orgEvents: {
    provider: 'orgEvents' as const,
    source: ORG_EVENTS_IN_AREA_SOURCE,
    title: 'Events in area',
    runLabel: 'Run Events',
    emptyHint: 'Set the report area in the header, then click Run Events.',
  },
  incidents: {
    provider: 'incidents' as const,
    source: INCIDENTS_IN_AREA_SOURCE,
    title: 'Incidents in area',
    runLabel: 'Run Incidents',
    emptyHint:
      'Set the report area in the header, then click Run Incidents to list incidents inside it or assigned to the AOR.',
  },
  actions: {
    provider: 'actions' as const,
    source: ACTIONS_IN_AREA_SOURCE,
    title: 'Tasks in area',
    runLabel: 'Run Tasks',
    emptyHint: 'Set the report area in the header, then click Run Tasks.',
  },
};

export function isAutomatedOrgEventsSection(
  section: SitrepSectionLike | null | undefined
): boolean {
  return (
    section?.type === 'automated' &&
    (section.automation?.provider === 'orgEvents' ||
      section.output_format_structured?.automation?.provider === 'orgEvents')
  );
}

export function isAutomatedIncidentsSection(
  section: SitrepSectionLike | null | undefined
): boolean {
  return (
    section?.type === 'automated' &&
    (section.automation?.provider === 'incidents' ||
      section.output_format_structured?.automation?.provider === 'incidents')
  );
}

export function isAutomatedActionsSection(
  section: SitrepSectionLike | null | undefined
): boolean {
  return (
    section?.type === 'automated' &&
    (section.automation?.provider === 'actions' ||
      section.output_format_structured?.automation?.provider === 'actions')
  );
}

type AutomatedEntityListSectionPanelProps = {
  sitrepId: string;
  section: SitrepSectionLike;
  sitrep?: Pick<GetSitrepsResponse, 'aor_id' | 'incident_id'>;
  selectedDatetime?: string;
  disabled?: boolean;
  kind: EntityListKind;
  dataStringOverride?: string | null;
  hideRun?: boolean;
  useLiveEnrichment?: boolean;
};

export default function AutomatedEntityListSectionPanel({
  sitrepId,
  section,
  sitrep,
  selectedDatetime,
  disabled = false,
  kind,
  dataStringOverride,
  hideRun = false,
  useLiveEnrichment = true,
}: AutomatedEntityListSectionPanelProps) {
  const aorText = useAorText();
  const config = KIND_CONFIG[kind];
  const updateSitrepSectionMutation = useUpdateSitrepSectionV2();
  const refreshSitrepMutation = useRefreshSitrepV2();
  const version = section.section_versions?.[0] || null;
  const payload = useMemo(
    () =>
      parseEntityListPayload(
        dataStringOverride !== undefined
          ? dataStringOverride
          : version?.data_string
      ),
    [dataStringOverride, version?.data_string]
  );

  const isProcessing = useMemo(() => {
    if (!version) return false;
    const status = (version.status || '').toLowerCase();
    return (
      !!status &&
      status !== 'completed' &&
      status !== 'summarized' &&
      status !== 'error'
    );
  }, [version]);

  const isBusy =
    disabled ||
    isProcessing ||
    updateSitrepSectionMutation.isPending ||
    refreshSitrepMutation.isPending;

  const handleRun = () => {
    const automation = {
      ...(section.automation ||
        section.output_format_structured?.automation ||
        {}),
      provider: config.provider,
    };
    updateSitrepSectionMutation.mutate(
      {
        sitrepId,
        sectionId: section.id,
        updatedFields: {
          type: 'automated',
          data_sources: [{ source: config.source }],
          automation,
          output_format_structured: {
            ...section.output_format_structured,
            automation,
          },
        },
      },
      {
        onSuccess: () => {
          refreshSitrepMutation.mutate(
            {
              id: sitrepId,
              selectedDatetime:
                selectedDatetime ||
                version?.timestamp ||
                new Date().toISOString(),
              sections: [section.id],
            },
            {
              onError: (error) => {
                toast.error(`Failed to refresh ${config.title}.`);
                console.error(`Error refreshing ${config.title}:`, error);
              },
            }
          );
        },
        onError: (error) => {
          toast.error(`Failed to save ${config.title}.`);
          console.error(`Error saving ${config.title}:`, error);
        },
      }
    );
  };

  return (
    <div className="AutomatedEntityListSectionPanel tw:rounded-lg tw:border tw:border-border tw:bg-background tw:p-3 tw:text-foreground">
      <div className="mb-3">
        <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
          <div>
            <strong>{config.title}</strong>
            <small className="tw:mt-0.5 tw:block tw:text-muted-foreground">
              {aorText(config.emptyHint)}
            </small>
          </div>
          {hideRun ? null : (
            <Button type="button" disabled={isBusy} onClick={handleRun}>
              {config.runLabel}
            </Button>
          )}
        </div>
      </div>
      {isProcessing ? (
        <ReportsGeneratingPlaceholder />
      ) : kind === 'orgEvents' ? (
        <OrgEventsResults
          payload={payload as OrgEventsPayload | null}
          emptyHint={aorText(config.emptyHint)}
          useLiveEnrichment={useLiveEnrichment}
        />
      ) : kind === 'actions' ? (
        <ActionsResults
          payload={payload as ActionsPayload | null}
          emptyHint={aorText(config.emptyHint)}
          incidentId={sitrep?.incident_id}
          useLiveEnrichment={useLiveEnrichment}
        />
      ) : (
        <IncidentsResults
          payload={payload as IncidentsPayload | null}
          emptyHint={aorText(config.emptyHint)}
          useLiveEnrichment={useLiveEnrichment}
        />
      )}
    </div>
  );
}
